import { formatYearMonth, formatWeekdays } from "../i18n/dateFormat";
import { atMidnight, DateValue, parseDateInput, plusMonths, toDate } from "../utils/Dates";
import { IconButton } from "./Button";
import { classNames } from "../utils/classNames";
import { Fragment, MouseEventHandler, useMemo, useState } from "react";
import { Language } from "../i18n/languages";
import { useLanguage, useTranslation } from "../stores/RootStoreContext";
import { Calendar, CalendarDay, CalendarParams, makeCalendar } from "../calendar/calendar";

function formatMonth(lang: Language, m: DateValue) {
  return formatYearMonth(lang, m);
}

function dayKey(day: CalendarDay) {
  return toDate(day.date).getTime();
}

function formatDay(day: CalendarDay) {
  return toDate(day.date).getDate();
}

function prepParams(params: { input: string; month?: Date }): CalendarParams {
  const parsedInput = parseDateInput(params.input ?? "");
  const today = atMidnight(Date.now());
  const month = params.month ?? parsedInput.parsed ?? today;
  return { month, today, selected: parsedInput.parsed };
}

function nextMonth({ month }: { month: DateValue }): Date {
  return plusMonths(month, 1);
}

function prevMonth({ month }: { month: DateValue }): Date {
  return plusMonths(month, -1);
}

export function DatePickerUI({
  calendar,
  onSelect,
  onNextMonth,
  onPrevMonth,
}: {
  calendar: Calendar;
  onSelect?: (date: DateValue) => void;
  onNextMonth: MouseEventHandler<HTMLButtonElement>;
  onPrevMonth: MouseEventHandler<HTMLButtonElement>;
}) {
  const locale = useLanguage();
  const { t } = useTranslation();

  return (
    <div className={"tw-max-w-fit bg-default"}>
      <div className={"tw-flex tw-items-center"}>
        <IconButton icon={"caret-left"} tooltip={t("prev-month")} onClick={onPrevMonth} />
        <div className={"tw-mx-auto tw-font-bold"}>{formatMonth(locale, calendar.month)}</div>
        <IconButton icon={"caret-right"} tooltip={t("next-month")} onClick={onNextMonth} />
      </div>
      <div className={"tw-grid tw-grid-cols-7 tw-justify-items-center tw-gap-0.5"}>
        {formatWeekdays(locale, "narrow").map((d, i) => (
          <div key={i} className={"subtle"}>
            {d}
          </div>
        ))}
        {calendar.weeks.map(({ week, days }) => (
          <Fragment key={week}>
            {days.map((day) => (
              <div key={dayKey(day)}>
                {!day.isOutsideMonth && (
                  <button
                    className={classNames(
                      "tw-w-10",
                      "tw-h-10",
                      "tw-flex",
                      "tw-items-center",
                      "tw-justify-center",
                      "tw-rounded-full",
                      day.isSelected
                        ? "bg-primary color-primary-contrast"
                        : "hover:bg-primary-light",
                      day.isToday && "tw-font-bold",
                    )}
                    onClick={() => onSelect?.(day.date)}
                  >
                    {formatDay(day)}
                  </button>
                )}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export type DatePickerProps = {
  input: string;
  onSelect?: (date: DateValue) => void;
};

export function DatePicker({ input, onSelect }: DatePickerProps) {
  const [month, setMonth] = useState<Date>();
  const calendar = useMemo<Calendar>(
    () => makeCalendar(prepParams({ month, input })),
    [month, input],
  );

  return (
    <DatePickerUI
      calendar={calendar}
      onSelect={onSelect}
      onNextMonth={(event) => {
        event.stopPropagation();
        setMonth(nextMonth(calendar));
      }}
      onPrevMonth={(event) => {
        event.stopPropagation();
        setMonth(prevMonth(calendar));
      }}
    />
  );
}
